// src/pages/portal/PortalConversacionesPage.tsx
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePortalConversaciones, usePortalEmpresas } from '@/hooks/queries';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  EmptyState,
} from '@/components/ui';
import { Plus, MessageSquare, Eye } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

function ConversacionesSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} className="h-20" />
      ))}
    </div>
  );
}

export default function PortalConversacionesPage() {
  const navigate = useNavigate();
  const [empresaId, setEmpresaId] = useState<string>('');
  const [estado, setEstado] = useState<string>('');

  const { data: empresas } = usePortalEmpresas();
  const { data: conversacionesData, isLoading, isError } = usePortalConversaciones({
    empresa_id: empresaId && empresaId !== '__all__' ? empresaId : undefined,
    estado: estado && estado !== '__all__' ? estado : undefined,
  });

  const conversaciones = conversacionesData?.items || [];

  const estadoBadgeVariant = (estado: string) => {
    switch (estado?.toLowerCase()) {
      case 'activa':
      case 'abierta':
        return 'success';
      case 'pausada':
        return 'warning';
      case 'cerrada':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Mis Conversaciones
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Chatea con nuestro equipo de soporte
          </p>
        </div>
        <Button onClick={() => navigate('/portal/conversaciones/nueva')}>
          <Plus className="w-4 h-4 mr-2" />
          Nueva Conversacion
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {empresas && empresas.length > 1 && (
              <Select value={empresaId} onValueChange={setEmpresaId}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Todas las empresas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas las empresas</SelectItem>
                  {empresas.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los estados</SelectItem>
                <SelectItem value="activa">Activa</SelectItem>
                <SelectItem value="pausada">Pausada</SelectItem>
                <SelectItem value="cerrada">Cerrada</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Lista de conversaciones */}
      <Card>
        <CardHeader>
          <CardTitle>Conversaciones ({conversacionesData?.total || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <ConversacionesSkeleton />
          ) : isError ? (
            <div className="text-center py-8">
              <p className="text-red-600 dark:text-red-400">Error al cargar las conversaciones</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Intenta recargar la pagina</p>
            </div>
          ) : conversaciones.length === 0 ? (
            <EmptyState
              icon={MessageSquare}
              title="No tienes conversaciones"
              description="Inicia una nueva conversacion con nuestro equipo de soporte."
              action={
                <Button onClick={() => navigate('/portal/conversaciones/nueva')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Conversacion
                </Button>
              }
            />
          ) : (
            <div className="space-y-3">
              {conversaciones.map((conv) => (
                <Link
                  key={conv.id}
                  to={`/portal/conversaciones/${conv.id}`}
                  className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={estadoBadgeVariant(conv.estado)}>
                          {conv.estado}
                        </Badge>
                        {conv.no_leidos > 0 && (
                          <Badge variant="danger">
                            {conv.no_leidos} nuevos
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">
                        {conv.asunto || 'Sin asunto'}
                      </h3>
                      {conv.ultimo_mensaje && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {conv.ultimo_mensaje}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        {conv.ultimo_mensaje_fecha
                          ? formatDistanceToNow(new Date(conv.ultimo_mensaje_fecha), {
                              addSuffix: true,
                              locale: es,
                            })
                          : formatDistanceToNow(new Date(conv.fecha_creacion), {
                              addSuffix: true,
                              locale: es,
                            })}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
